import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaCalendarCheck } from 'react-icons/fa';
import heroBg from '../assets/images/hero-bg.jpg';
import './Hero.css';

/* ── Animation variants ── */
const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15, delayChildren: 0.2 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: 'easeOut' } },
};

/* ── Animated counter hook ── */
function useCounter(target, duration = 1800, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);
  return count;
}

function Stat({ value, suffix, label, inView }) {
  const count = useCounter(value, 1800, inView);
  return (
    <div className="hero__stat">
      <span className="hero__stat-num">{count}{suffix}</span>
      <span className="hero__stat-label">{label}</span>
    </div>
  );
}

export default function Hero() {
  const statsRef = useRef(null);
  const [statsInView, setStatsInView] = useState(false);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsInView(true); }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleWhatsApp = () => {
    window.open('https://wa.me/918605246832?text=Hi%2C%20I%20want%20to%20start%20my%20business%20with%20RRP%20%26%20Associates', '_blank');
  };

  const scrollToContact = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="hero" id="home" style={{ backgroundImage: `url(${heroBg})` }}>
      {/* Dark navy overlay */}
      <div className="hero__overlay" />
      <div className="hero__bg" />

      <div className="container hero__content">
        <motion.div variants={container} initial="hidden" animate="show" className="hero__text">

          {/* Badge */}
          <motion.div variants={fadeUp} className="hero__badge">
            🏆 Trusted by 500+ Businesses Across India
          </motion.div>

          {/* Headline */}
          <motion.h1 variants={fadeUp} className="hero__headline">
            Start Your Business<br />
            <span className="hero__headline--gold">Without Confusion</span> 🚀
          </motion.h1>

          {/* Subheadline */}
          <motion.p variants={fadeUp} className="hero__sub">
            We handle registration, compliance, branding & growth —<br className="hero__br" />
            so you can focus on what matters.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div variants={fadeUp} className="hero__ctas">
            <motion.button
              className="btn-primary hero__btn"
              onClick={scrollToContact}
              whileHover={{ scale: 1.04, boxShadow: '0 8px 28px rgba(201,168,76,0.5)' }}
              whileTap={{ scale: 0.97 }}
            >
              <FaCalendarCheck /> Book Free Consultation
            </motion.button>
            <motion.button
              className="btn-outline hero__btn"
              onClick={handleWhatsApp}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
            >
              <FaWhatsapp size={18} /> WhatsApp Now
            </motion.button>
          </motion.div>

          {/* Stats row */}
          <motion.div variants={fadeUp} className="hero__stats" ref={statsRef}>
            <Stat value={500} suffix="+" label="Businesses Registered" inView={statsInView} />
            <div className="hero__stat-divider" />
            <Stat value={5}   suffix="+" label="Years Experience"       inView={statsInView} />
            <div className="hero__stat-divider" />
            <Stat value={100} suffix="%" label="Compliance Guaranteed"  inView={statsInView} />
            <div className="hero__stat-divider" />
            <div className="hero__stat">
              <span className="hero__stat-num">₹3,999</span>
              <span className="hero__stat-label">Starting Price</span>
            </div>
          </motion.div>

        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        className="hero__scroll-cue"
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        ↓
      </motion.div>
    </section>
  );
}
